"use strict";
// SPDX-FileCopyrightText: 2024 Red Bee Media Ltd <https://www.redbeemedia.com/>
//
// SPDX-License-Identifier: MIT
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AdMonitor = void 0;
const js_player_shared_1 = require("@ericssonbroadcastservices/js-player-shared");
const InternalAdsParser_1 = require("./parsers/InternalAdsParser");
const NowtilusAdsParser_1 = require("./parsers/NowtilusAdsParser");
const constants_1 = require("./utils/constants");
// How many ms between time updates until an ad is no longer considered playing linearly
const LINEAR_AD_THRESHOLD = 2000;
/**
 * AdMonitor
 * The monitor is responsible for monitoring the stream and report back about
 * what ad related activity is going on in the stream given the current time.
 * It is also reponsible of sending any necessary tracking events for running ads.
 */
class AdMonitor extends js_player_shared_1.EmitterBaseClass {
    constructor(format, ads) {
        super();
        this.adBlocks = [];
        this.trackedAds = {};
        this.format = format;
        this.ads = ads;
        this.adStitchEngine = this.resolveEngine(ads.stitcher);
        if (!this.adStitchEngine) {
            console.warn(ads.stitcher
                ? `[AdMonitor] Stitcher: ${ads.stitcher} is not supported`
                : "[AdMonitor] Stitcher is not defined");
            return;
        }
        this.adStitchEngine.on(constants_1.StitcherEvent.ADBLOCK, (adBlock) => this.addAdBlock(adBlock));
        this.adStitchEngine.init();
    }
    resolveEngine(adStitchProvider) {
        switch (adStitchProvider) {
            case "NOWTILUS":
                return new NowtilusAdsParser_1.NowtilusAdsParser(this.format, this.ads);
            case "INTERNAL":
                return new InternalAdsParser_1.InternalAdsParser(this.ads.clips);
            default:
                break;
        }
    }
    handleTimeUpdate({ utcCurrentTime, currentTime, }) {
        if (!this.adStitchEngine || !this.adBlocks.length) {
            return;
        }
        const currentTimeMs = this.adStitchEngine.isTimeUTC()
            ? utcCurrentTime
            : currentTime * 1000;
        // check if an adblock has started
        if (!this.currentAdBlock) {
            const adBlock = this.adBlocks.find((adBlock) => currentTimeMs >= adBlock.startTime && currentTimeMs < adBlock.endTime);
            if (!adBlock) {
                return;
            }
            this.currentAdBlock = adBlock;
            this.emit(constants_1.AdMonitorEvent.ADBLOCK_START, Object.assign({}, this.currentAdBlock));
        }
        const currentAdBlock = this.currentAdBlock;
        let durationAccuracy = 0;
        // get the ads stored from the vast response
        const ads = this.currentAdBlock.ads;
        ads.forEach((ad) => {
            var _a;
            // calculate when the ad starts and stops in relation to the vast response time
            const adStartTime = currentAdBlock.startTime + durationAccuracy;
            const adEndTime = currentAdBlock.startTime + durationAccuracy + ad.duration;
            durationAccuracy += ad.duration;
            // if current time is within ad start and ad end, it's an ongoing ad
            if (currentTimeMs >= adStartTime && currentTimeMs < adEndTime) {
                const remainingTime = adEndTime - currentTimeMs;
                const percentWatched = Math.round(100 - (100 * remainingTime) / ad.duration);
                // report end of the current ad ( if any ) and report start of the new one
                if (((_a = this.currentAd) === null || _a === void 0 ? void 0 : _a.id) !== ad.id) {
                    if (this.currentAd) {
                        this.emit(constants_1.AdMonitorEvent.AD_END, Object.assign({}, this.currentAd));
                        this.trackEvent(this.currentAd, js_player_shared_1.TrackingEvent.COMPLETE);
                    }
                    this.currentAd = ad;
                    const adIndex = ads.indexOf(ad) + 1;
                    const adsTotal = ads.length;
                    this.emit(constants_1.AdMonitorEvent.AD_START, Object.assign(Object.assign({}, ad), { adIndex,
                        adsTotal }));
                    this.trackEvent(ad, js_player_shared_1.TrackingEvent.LOADED);
                    this.trackEvent(ad, js_player_shared_1.TrackingEvent.START);
                    this.trackEvent(ad, js_player_shared_1.TrackingEvent.IMPRESSION);
                }
                Object.keys(constants_1.TrackingCuePoints).map((value) => {
                    const event = constants_1.TrackingCuePoints[value];
                    if (percentWatched > parseInt(value, 10)) {
                        this.trackEvent(ad, event);
                    }
                });
            }
        });
        // Check if the adblock has ended
        if (currentTimeMs > Math.floor(currentAdBlock.endTime)) {
            const isEndingLinear = currentTimeMs - this.currentAdBlock.endTime < LINEAR_AD_THRESHOLD;
            if (this.currentAd && isEndingLinear) {
                this.emit(constants_1.AdMonitorEvent.AD_END, Object.assign({}, this.currentAd));
                this.trackEvent(this.currentAd, js_player_shared_1.TrackingEvent.COMPLETE);
            }
            // TODO: is there a scenario where `this.currentAdBlock` is undefined here?
            // should we only emit ADBLOCK_END if we have an adblock ending...
            this.emit(constants_1.AdMonitorEvent.ADBLOCK_END, Object.assign({}, this.currentAdBlock));
            this.currentAdBlock = undefined;
            this.currentAd = undefined;
        }
    }
    handleEnded() {
        if (this.currentAd) {
            this.emit(constants_1.AdMonitorEvent.AD_END, Object.assign({}, this.currentAd));
            this.trackEvent(this.currentAd, js_player_shared_1.TrackingEvent.COMPLETE);
        }
        this.emit(constants_1.AdMonitorEvent.ADBLOCK_END, this.currentAdBlock ? Object.assign({}, this.currentAdBlock) : undefined);
        this.currentAdBlock = undefined;
        this.currentAd = undefined;
    }
    trackEvent(ad, trackingEvent) {
        if (!ad ||
            !trackingEvent ||
            (this.trackedAds[ad.id] && this.trackedAds[ad.id][trackingEvent] === true)) {
            return;
        }
        if (!this.trackedAds[ad.id]) {
            this.trackedAds[ad.id] = {};
        }
        const trackingUrls = ad.trackingEvents[trackingEvent];
        trackingUrls === null || trackingUrls === void 0 ? void 0 : trackingUrls.forEach((url) => __awaiter(this, void 0, void 0, function* () {
            // send and forget
            new Image().src = url;
        }));
        this.trackedAds[ad.id][trackingEvent] = true;
    }
    getAdBlocks() {
        return this.adBlocks;
    }
    addAdBlock(adBlock) {
        if (!adBlock.ads.length ||
            this.adBlocks.find((block) => block.startTime === adBlock.startTime)) {
            return;
        }
        this.adBlocks.push(adBlock);
        this.emit(constants_1.AdMonitorEvent.ADBLOCK_ADDED, adBlock);
    }
    destroy() {
        var _a;
        super.destroy();
        (_a = this.adStitchEngine) === null || _a === void 0 ? void 0 : _a.destroy();
        this.adStitchEngine = undefined;
        this.currentAd = undefined;
        this.currentAdBlock = undefined;
    }
}
exports.AdMonitor = AdMonitor;
//# sourceMappingURL=AdMonitor.js.map